import { useHoverState } from '@engoo/react-hover-state';
import { BaseButtonUnstyled } from 'app/components/common/BaseButton';
import { GREY_F, PURE_BLACK, PURE_WHITE, WHITE_A } from 'app/constants/Colors';
import FocusOutlineState from 'app/containers/state/FocusOutlineState';
import { IStyles, useCss } from 'app/utils/css';
import { CallbackRef, useCombineCallbackRefs } from 'app/utils/react';
import React, { FC, FunctionComponent } from 'react';

interface IBaseProps {
  getRef?: CallbackRef;
  onClick: (event: React.MouseEvent) => void;
  selected?: boolean;
}

interface IDeps {
  focusableRef: CallbackRef;
  showOutline: boolean;
}

interface IProps extends IBaseProps, IDeps {}

/**
 * Purely presentational component for things that should look like they are selectable (e.g. show a
 * pointer cursor, change colors on hover, etc.).
 */
const Selectable: FunctionComponent<IProps> = ({
  selected = false,
  getRef,
  children,
  onClick,
  showOutline,
  focusableRef,
}) => {
  const [isHovered, hoverRef] = useHoverState();

  const baseClass: IStyles = {
    cursor: 'pointer',
    padding: '5px 15px',
    color: PURE_BLACK,
    backgroundColor: PURE_WHITE,
    display: 'block',
    appearance: 'none',
    border: '0',
    textAlign: 'left',
    width: '100%',

    ...(selected
      ? { backgroundColor: GREY_F }
      : isHovered
      ? { backgroundColor: WHITE_A }
      : {
        ':active': {
          backgroundColor: GREY_F,
        },
        ':focus': {
          backgroundColor: GREY_F,
        },
      }),
  };

  const css = useCss();
  const ref = useCombineCallbackRefs([getRef, focusableRef, hoverRef]);

  return (
    <BaseButtonUnstyled
      className={css('button', {
        ...baseClass,
        ...(showOutline
          ? {
              // This is to make sure outline is not hidden underneath other elements. In general,
              // focused elements should not be hidden or obscured, so this should be safe
              // to do here.
            position: 'relative',
            zIndex: 1,
          }
          : {
            outline: 'none',
          }),
      })}
      getRef={ref}
      onClick={onClick}
    >
      {children}
    </BaseButtonUnstyled>
  );
};

const SelectableInjectDependencies: FC<IBaseProps> = props => {
  return (
    <FocusOutlineState>
      {({ showOutline, focusableRef }) => (
        <Selectable focusableRef={focusableRef} showOutline={showOutline} {...props} />
      )}
    </FocusOutlineState>
  );
};

export default SelectableInjectDependencies;
